import React from 'react';
import { X, BookOpen, Target, Clock } from 'lucide-react';
import { formatDuration } from '@/utils/timeFormat';

interface CourseOverviewModalProps {
  course: any;
  isOpen: boolean;
  onClose: () => void;
  onSelectModule?: (module: any) => void;
  isScormCourse?: boolean;
}

export default function CourseOverviewModal({
  course,
  isOpen,
  onClose,
  onSelectModule,
  isScormCourse = false
}: CourseOverviewModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col m-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-blue-600 to-brand-purple-600 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{course?.title}</h2>
              {course?.description && (
                <p className="text-white/90 leading-relaxed">{course.description}</p>
              )}
              <div className="flex items-center gap-6 mt-4">
                {!isScormCourse && course?.estimated_duration?.total && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {formatDuration(course.estimated_duration.total)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-sm font-medium">{course?.modules?.length || 0} Modules</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Learning Objectives */}
          {course?.learning_objectives && course.learning_objectives.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-600" />
                Learning Objectives
              </h3>
              <div className="space-y-3">
                {course.learning_objectives.map((objective: any, index: number) => (
                  <div key={objective.id || index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-purple-600">{index + 1}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {objective.description || objective.text || objective.objective}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Modules List */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Course Modules</h3>
            <div className="space-y-3">
              {course?.modules?.map((module: any) => (
                <button
                  key={module._id}
                  onClick={() => {
                    onSelectModule?.(module);
                    onClose();
                  }}
                  className="w-full text-left p-4 bg-gray-50 hover:bg-purple-50 border border-gray-200 hover:border-purple-300 rounded-lg transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Module {module.module_number}: {module.title}
                      </h4>
                      {module.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">{module.description}</p>
                      )}
                    </div>
                    {!isScormCourse && module.estimated_duration && (
                      <div className="flex items-center gap-1 text-sm text-gray-500 ml-4">
                        <Clock className="w-4 h-4" />
                        {formatDuration(module.estimated_duration)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
